/**
 * EmbeddedRouter — an isolated, in-memory router for an embedded plugin
 * view, renderable inside the host's BrowserRouter.
 *
 * react-router v7 refuses to render a <Router> inside another one (it checks
 * LocationContext), so this resets LocationContext and RouteContext before a
 * controlled <Router>. All `UNSAFE_` usage is confined to this file and
 * pinned by tests; a react-router upgrade that changes it fails them loudly.
 *
 * Navigation to one of the plugin's own routes stays inside the frame;
 * anything else is handed to `onExternal` (host navigate, block, emit …).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Route,
  Router,
  Routes,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
  createPath,
  parsePath,
} from 'react-router-dom';
import type { Location, Navigator, To } from 'react-router-dom';
import { NavigationType } from 'react-router-dom';

export interface EmbeddedRouterProps {
  initialPath: string;
  /** Route patterns of the owning plugin; matching paths navigate in-frame. */
  internalPatterns: string[];
  isInternal: (path: string) => boolean;
  /** Called for navigations outside the plugin's routes. */
  onExternal: (path: string, replace: boolean) => void;
  onLocationChange?: (path: string) => void;
  /** Imperative in-frame navigation (controller.navigate). */
  navigateRef?: React.MutableRefObject<((path: string) => void) | null>;
  children: React.ReactNode;
}

interface HistoryState {
  entries: string[];
  index: number;
  action: NavigationType;
}

let keyCounter = 0;

function toPath(to: To): string {
  return typeof to === 'string' ? to : createPath(to);
}

export function EmbeddedRouter({
  initialPath, internalPatterns, isInternal, onExternal, onLocationChange, navigateRef, children,
}: EmbeddedRouterProps) {
  const [history, setHistory] = useState<HistoryState>({ entries: [initialPath], index: 0, action: NavigationType.Pop });
  const keys = useRef<string[]>([`embed-${++keyCounter}`]);
  const onExternalRef = useRef(onExternal);
  onExternalRef.current = onExternal;
  const isInternalRef = useRef(isInternal);
  isInternalRef.current = isInternal;

  const pushOrReplace = useCallback((to: To, replace: boolean) => {
    const path = toPath(to);
    if (!isInternalRef.current(path)) {
      onExternalRef.current(path, replace);
      return;
    }
    setHistory((h) => {
      if (replace) {
        const entries = [...h.entries];
        entries[h.index] = path;
        keys.current[h.index] = `embed-${++keyCounter}`;
        return { entries, index: h.index, action: NavigationType.Replace };
      }
      const entries = [...h.entries.slice(0, h.index + 1), path];
      keys.current = [...keys.current.slice(0, h.index + 1), `embed-${++keyCounter}`];
      return { entries, index: entries.length - 1, action: NavigationType.Push };
    });
  }, []);

  const navigator = useMemo<Navigator>(() => ({
    createHref: (to: To) => toPath(to),
    encodeLocation: (to: To) => {
      const parsed = typeof to === 'string' ? parsePath(to) : to;
      return { pathname: parsed.pathname ?? '/', search: parsed.search ?? '', hash: parsed.hash ?? '' };
    },
    go: (delta: number) => setHistory((h) => {
      const index = Math.min(h.entries.length - 1, Math.max(0, h.index + delta));
      return index === h.index ? h : { ...h, index, action: NavigationType.Pop };
    }),
    push: (to: To) => pushOrReplace(to, false),
    replace: (to: To) => pushOrReplace(to, true),
  }), [pushOrReplace]);

  if (navigateRef) navigateRef.current = (path: string) => pushOrReplace(path, false);

  const current = history.entries[history.index];
  const location = useMemo<Location>(() => {
    const parsed = parsePath(current);
    return {
      pathname: parsed.pathname || '/',
      search: parsed.search ?? '',
      hash: parsed.hash ?? '',
      state: null,
      key: keys.current[history.index] ?? 'default',
    };
  }, [current, history.index]);

  useEffect(() => { onLocationChange?.(current); }, [current, onLocationChange]);

  return (
    <UNSAFE_LocationContext.Provider value={null as unknown as React.ContextType<typeof UNSAFE_LocationContext>}>
      <UNSAFE_RouteContext.Provider value={{ outlet: null, matches: [], isDataRoute: false }}>
        <Router location={location} navigationType={history.action} navigator={navigator}>
          <Routes>
            {internalPatterns.map((pattern) => <Route key={pattern} path={pattern} element={children} />)}
            <Route path="*" element={children} />
          </Routes>
        </Router>
      </UNSAFE_RouteContext.Provider>
    </UNSAFE_LocationContext.Provider>
  );
}
